import httpService from "./http.service";

function options(sessionToken) {
  const token = localStorage.getItem("token");
  return {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(sessionToken ? { "X-Onboarding-Session-Token": sessionToken } : {}),
    },
  };
}

function data(body) {
  if (!body || body.success !== true || !body.data) {
    throw new Error(body?.message || body?.msg || body?.error || "Invalid installation API response");
  }
  return body.data;
}

const service = {
  routing: async (code) => data(await httpService.get(
    `/onboarding/public/claims/${encodeURIComponent(code)}`,
    { headers: { "Content-Type": "application/json", "X-Onboarding-Session-Token": "" } },
  )),
  start: async (code) => data(await httpService.post(
    "/onboarding/claim/start", { claim_code: code }, options(),
  )),
  session: async (session) => data(await httpService.get(
    `/onboarding/session/${encodeURIComponent(session.session_id)}`, options(session.session_token),
  )),
  overview: async (session) => data(await httpService.get(
    `/onboarding/session/${encodeURIComponent(session.session_id)}/installation`,
    options(session.session_token),
  )),
  targets: async (session, entityId) => data(await httpService.get(
    `/onboarding/session/${encodeURIComponent(session.session_id)}/installation/targets?entity_id=${encodeURIComponent(entityId)}`,
    options(session.session_token),
  )),
  save: async (session, stepKey, payload) => data(await httpService.post(
    `/onboarding/session/${encodeURIComponent(session.session_id)}/step`,
    { step_key: stepKey, payload }, options(session.session_token),
  )),
  complete: async (session, payload) => data(await httpService.post(
    `/onboarding/session/${encodeURIComponent(session.session_id)}/complete`,
    payload, options(session.session_token),
  )),
  status: async (claimId) => data(await httpService.get(
    `/onboarding/claims/${encodeURIComponent(claimId)}/installation-status`, options(),
  )),
  // Active-session lookups for the target customer/asset (contacts, addresses,
  // per-asset access, alert metadata and the server's terms version).
  claimSetupOptions: async (session, { entityId, assetId } = {}) => {
    const params = new URLSearchParams();
    if (entityId) params.set("entity_id", String(entityId));
    if (assetId) params.set("asset_id", String(assetId));
    const query = params.toString();
    return data(await httpService.get(
      `/onboarding/session/${encodeURIComponent(session.session_id)}/installation/claim-setup-options${query ? `?${query}` : ""}`,
      options(session.session_token),
    ));
  },
  saveSetup: async (session, body) => data(await httpService.put(
    `/onboarding/session/${encodeURIComponent(session.session_id)}/installation/setup`,
    body, options(session.session_token),
  )),
  // Claim-scoped after completion: never call session endpoints once installed.
  applyOperations: async (claimId, operations) => data(await httpService.post(
    `/onboarding/claims/${encodeURIComponent(claimId)}/installation-operations`,
    { operations },
    {
      headers: {
        ...options().headers,
        ...(operations.length === 1 ? { "Idempotency-Key": operations[0].operation_id } : {}),
      },
    },
  )),
  summary: async (claimId) => data(await httpService.get(
    `/onboarding/claims/${encodeURIComponent(claimId)}/installation-summary`, options(),
  )),
  listClaims: async () => {
    const body = await httpService.get("/onboarding/claims", options());
    if (!body || body.success !== true || !Array.isArray(body.data)) {
      throw new Error(body?.message || "Invalid claims response");
    }
    return body.data;
  },
};

export function installationMessage(error) {
  if (typeof error?.message === "string") return error.message;
  if (typeof error?.msg === "string") return error.msg;
  if (typeof error?.error === "string") return error.error;
  return "Installation request failed. Please retry or contact support.";
}

export default service;